import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, ArrowRight } from "lucide-react";
import client from "../lib/api";

/**
 * Gate an assessment derived surface (snapshot, roadmap, careers,
 * learning, journal, resume, ask Klair, college, check in) behind a
 * completed assessment.
 *
 * The server is the source of truth. GET /demo/read/latest returns a
 * current read only once the assessment has produced one, so we confirm
 * with the server on every mount rather than trusting a cached flag. A
 * stale klareet.report left in a shared browser can therefore never open
 * the gate for a new person. On success we prime that cache so the
 * downstream screen paints without a second round trip.
 */
export default function AssessmentGate({ product, children }) {
  const base = product === "grow" ? "/grow" : "";
  const prod = product === "grow" ? "grow" : "start";
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    let alive = true;
    client
      .get(`/demo/read/latest?product=${prod}`)
      .then((r) => {
        if (!alive) return;
        const report = r && r.data ? r.data.report : null;
        if (report) {
          try {
            localStorage.setItem("klareet.report", JSON.stringify(report));
          } catch (_e) {
            /* cache is a soft optimisation; ignore write failures */
          }
          setStatus("open");
        } else {
          setStatus("locked");
        }
      })
      .catch(() => {
        // 404 (no person record) or a network error both mean we cannot
        // prove the assessment is done, so we fail closed and invite the
        // person to complete it.
        if (alive) setStatus("locked");
      });
    return () => {
      alive = false;
    };
  }, [prod]);

  if (status === "open") return children;

  if (status === "checking") {
    return (
      <div
        data-testid="assessment-gate-checking"
        aria-busy="true"
        style={{ minHeight: "40vh" }}
      />
    );
  }

  return (
    <div
      data-testid="assessment-gate-locked"
      style={{
        maxWidth: 560,
        margin: "10vh auto",
        padding: "2rem",
        textAlign: "center",
        color: "#1A202C",
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: "9999px",
          background: "rgba(245,132,31,0.12)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 1rem",
        }}
      >
        <Lock size={26} color="#F5841F" />
      </div>
      <h2 style={{ fontSize: "1.35rem", fontWeight: 700, margin: "0 0 0.5rem" }}>
        Finish your assessment to unlock this
      </h2>
      <p style={{ color: "#4A5568", margin: "0 0 1.5rem", lineHeight: 1.5 }}>
        This page is built from your assessment. Complete it with Klair and
        your snapshot, roadmap, careers and more open up right here.
      </p>
      <Link
        to={`${base}/welcome`}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "0.5rem",
          background: "#F5841F",
          color: "#ffffff",
          fontWeight: 600,
          padding: "0.75rem 1.25rem",
          borderRadius: "0.6rem",
          textDecoration: "none",
        }}
      >
        Start your assessment
        <ArrowRight size={18} />
      </Link>
    </div>
  );
}
